import React from "react";
import { Box, Grid, HStack, Icon, Stack, Text, Tooltip } from "@chakra-ui/react";
import { MdAssignment, MdAttachMoney, MdHowToReg, MdLocalCafe, MdRecordVoiceOver, MdSports } from "react-icons/md";
import { DataCard } from "components/utils/DataCard";
import { FormDialog } from "components/utils/FormDialog";
import { DashboardTruncatedText } from "components/utils/FormControls";
import AppAvatar from "components/utils/AppAvatar";
import { useDialogForm } from "utils/useDialogForm";
import { getAvatarHexColor, getAvatarTint, resolveAvatarUrl } from "utils/avatar";
import { useDashboardActividadEncargos, usePatchDashboardActividadEncargo } from "features/dashboard/actividades/hooks";
import { CompactEmojiRatingPicker } from "../components/CompactEmojiRatingPicker";
import { emojiForRating, labelForRating } from "../components/EmojiRatingBar";
import { asArray, toNullableInteger } from "../shared/helpers";

const ROLE_OPTIONS = [
  { id: "actividad", label: "Actividad", icon: MdSports, iconColor: "arawaks.skyBlue" },
  { id: "charla", label: "Charla", icon: MdRecordVoiceOver, iconColor: "arawaks.grapePurple" },
  { id: "refrigerio", label: "Refrigerio", icon: MdLocalCafe, iconColor: "arawaks.adventureOrange" },
  { id: "cobranza", label: "Cobranza", icon: MdAttachMoney, iconColor: "arawaks.jungleGreen" },
  { id: "asistencia", label: "Asistencia", icon: MdHowToReg, iconColor: "arawaks.campfireRed" },
];

function hasRating(value) {
  return value != null && value !== "" && Number.isFinite(Number(value));
}

function rowTone(seed) {
  const solid = getAvatarHexColor(seed);
  return { solid, bg: getAvatarTint(seed, 12) };
}

export const PosteriorDesempenoCard = ({ actividadId }) => {
  const dialog = useDialogForm({ rows: [] });
  const [localError, setLocalError] = React.useState(null);

  const encargosQ = useDashboardActividadEncargos(actividadId, !!actividadId);
  const patchEncargo = usePatchDashboardActividadEncargo();

  const saving = patchEncargo.isPending;
  const apiError = patchEncargo.isError ? String(patchEncargo.error?.message || patchEncargo.error) : null;
  const errorText = localError || apiError;

  const roleById = React.useMemo(() => new Map(ROLE_OPTIONS.map((r) => [r.id, r])), []);

  const rowsFromDb = React.useMemo(
    () =>
      asArray(encargosQ.data).map((row) => ({
        id: row.id,
        instructorId: row.instructorId,
        instructorNombre: row.instructorNombre || "Instructor",
        fotoUrl: resolveAvatarUrl(row),
        encargo: row.encargo,
        calificacion: row.calificacion == null ? null : Number(row.calificacion),
      })),
    [encargosQ.data]
  );

  const isIncomplete = React.useMemo(
    () => rowsFromDb.some((row) => !hasRating(row.calificacion)),
    [rowsFromDb]
  );

  function openDialog() {
    setLocalError(null);
    dialog.openWith({
      rows: rowsFromDb.map((row) => ({
        ...row,
        calificacion: row.calificacion == null ? "" : String(row.calificacion),
      })),
    });
  }

  function patchRow(rowId, patch) {
    dialog.setField(
      "rows",
      asArray(dialog.form.rows).map((row) => (row.id === rowId ? { ...row, ...patch } : row))
    );
  }

  async function save() {
    setLocalError(null);

    const prevById = new Map(rowsFromDb.map((row) => [row.id, row]));
    const updates = asArray(dialog.form.rows)
      .map((row) => {
        const prev = prevById.get(row.id);
        if (!prev) return null;
        const nextCalificacion = toNullableInteger(row.calificacion);
        if (nextCalificacion === prev.calificacion) return null;
        return { encargoId: row.id, calificacion: nextCalificacion };
      })
      .filter(Boolean);

    if (updates.length === 0) {
      dialog.close();
      return;
    }

    try {
      for (const update of updates) {
        await patchEncargo.mutateAsync({
          actividadId,
          encargoId: update.encargoId,
          patch: { calificacion: update.calificacion },
        });
      }
      dialog.close();
    } catch (e) {
      setLocalError(String(e?.message || e));
      throw e;
    }
  }

  return (
    <>
      <DataCard
        title="Desempeño"
        variant="orange"
        headerIcon={<MdAssignment size="24px" />}
        onEdit={openDialog}
        isIncomplete={isIncomplete}
        editAriaLabel="Calificar desempeño"
      >
        {encargosQ.isLoading ? (
          <Text color="dashboard.fg.muted" fontSize="sm">Cargando encargos...</Text>
        ) : rowsFromDb.length === 0 ? (
          <Text color="dashboard.fg.muted" fontSize="sm">Sin encargos asignados en Previo.</Text>
        ) : (
          <Grid templateColumns={{ base: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" }} gap="2.5" minW="0">
            {rowsFromDb.map((row) => {
              const role = roleById.get(row.encargo);
              const RoleIcon = role?.icon;
              const roleLabel = role?.label || row.encargo || "Encargo";
              const nombre = row.instructorNombre || "Instructor";
              const tone = rowTone(row.instructorId || nombre);

              return (
                <Box key={row.id} rounded="2xl" p="3" bg={tone.bg} borderWidth="1px" borderColor="dashboard.border.muted" minW="0" overflow="hidden">
                  <HStack gap="2.5" align="start" minW="0">
                    <AppAvatar name={nombre} src={row.fotoUrl} seed={row.instructorId || nombre} />

                    <Stack gap="1" justify="center" minW="0" flex="1">
                      <HStack gap="2" minW="0" justify="space-between">
                        <DashboardTruncatedText fontWeight="800" fontSize="sm" color="dashboard.fg.default">
                          {nombre}
                        </DashboardTruncatedText>

                        {hasRating(row.calificacion) ? (
                          <Tooltip.Root openDelay={0}>
                            <Tooltip.Trigger asChild>
                              <Text fontSize="lg" lineHeight="1" cursor="default" flexShrink={0}>
                                {emojiForRating(row.calificacion)}
                              </Text>
                            </Tooltip.Trigger>
                            <Tooltip.Positioner>
                              <Tooltip.Content>{labelForRating(row.calificacion)}</Tooltip.Content>
                            </Tooltip.Positioner>
                          </Tooltip.Root>
                        ) : null}
                      </HStack>

                      <Box alignSelf="start" rounded="pill" px="2.5" py="0.5" bg={tone.solid} maxW="full">
                        <HStack gap="1.5" minW="0">
                          <Icon color="white" flexShrink={0}>{RoleIcon ? <RoleIcon /> : null}</Icon>
                          <Text as="span" color="white" fontSize="xs" fontWeight="800" truncate>
                            {roleLabel}
                          </Text>
                        </HStack>
                      </Box>
                    </Stack>
                  </HStack>
                </Box>
              );
            })}
          </Grid>
        )}
      </DataCard>

      <FormDialog
        open={dialog.open}
        onOpenChange={dialog.onOpenChange}
        title="Calificar Desempeño"
        errorText={errorText}
        saving={saving}
        onCancel={dialog.close}
        onSave={save}
      >
        <Box>
          {asArray(dialog.form.rows).length === 0 ? (
            <Text color="dashboard.fg.muted">No hay encargos para calificar.</Text>
          ) : (
            <Stack gap="2">
              <Grid templateColumns="2fr 2fr 1.5fr" gap="3" px="2" py="2" bg="dashboard.bg.surfaceMuted" rounded="md">
                <Text fontSize="sm" fontWeight="600" color="dashboard.fg.muted">Instructor</Text>
                <Text fontSize="sm" fontWeight="600" color="dashboard.fg.muted">Encargo</Text>
                <Text fontSize="sm" fontWeight="600" color="dashboard.fg.muted">Calificacion</Text>
              </Grid>

              {asArray(dialog.form.rows).map((row) => {
                const role = roleById.get(row.encargo);
                const RoleIcon = role?.icon;
                const roleLabel = role?.label || row.encargo || "Encargo";
                const roleIconColor = role?.iconColor || "dashboard.fg.muted";
                const nombre = row.instructorNombre || "Instructor";

                return (
                  <Grid key={row.id} templateColumns="2fr 2fr 1.5fr" gap="3" px="2" py="2" alignItems="center">
                    <HStack>
                      <AppAvatar size="sm" name={nombre} src={row.fotoUrl} seed={row.instructorId || nombre} />
                      <Text>{nombre}</Text>
                    </HStack>

                    <HStack gap="1.5">
                      <Icon color={roleIconColor}>{RoleIcon ? <RoleIcon /> : null}</Icon>
                      <Text>{roleLabel}</Text>
                    </HStack>

                    <CompactEmojiRatingPicker
                      value={String(row.calificacion || "")}
                      onChange={(v) => patchRow(row.id, { calificacion: v })}
                      isDisabled={saving}
                      ariaLabel={`Calificacion de ${nombre} en ${roleLabel}`}
                    />
                  </Grid>
                );
              })}
            </Stack>
          )}
        </Box>
      </FormDialog>
    </>
  );
};
